import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Lobby from "./Lobby";
import UserContext from "../../context/UserContext";
import UIContext from "../../context/UIContext";
import SocketContext from "../../context/SocketContext";
import sanitizeInput from "../../hooks/sanitizeInput";
import CreateRoomModal from "../modals/CreateRoomModal";
import Modal from "../common/Modal";
import { getOrCreateUserId } from "../../utils/userId";
import { traceLog } from "../../App";

function LobbyContainer({ lobbyData, joinRoomError, setJoinRoomError }) {
  const socket = useContext(SocketContext);
  const { user, setUser } = useContext(UserContext);
  const { setShowCreateRoomModal, showCreateRoomModal, joinRoom } =
    useContext(UIContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [gameType, setGameType] = useState("");
  const [roomName, setRoomName] = useState("");
  const [roomId, setRoomId] = useState("");

  const [allowSpectators, setAllowSpectators] = useState(true);
  const [requirePassword, setRequirePassword] = useState(false);
  const [roomPassword, setRoomPassword] = useState("");

  useEffect(() => {
    const requestedGameType = location.state?.createGameType;
    const shouldOpenCreateRoom = location.state?.openCreateRoom === true;

    if (!shouldOpenCreateRoom || !requestedGameType) return;

    const availableGameTypes = Array.isArray(lobbyData?.gameTypes)
      ? lobbyData.gameTypes
      : Object.values(lobbyData?.gameTypes || {});
    const matchedGameType = availableGameTypes.find(
      (game) => game?.gameClass === requestedGameType || game?.key === requestedGameType
    );

    if (!matchedGameType) return;

    setGameType(matchedGameType.gameClass || matchedGameType.key);
    setShowCreateRoomModal(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, lobbyData?.gameTypes, navigate, setShowCreateRoomModal]);

  // Create a room handler
  const createRoom = (opts = {}) => {
    setShowCreateRoomModal(false);

    // Prefer opts.roomName if provided, else fallback to local state
    const rawRoomName = opts.roomName !== undefined ? opts.roomName : roomName;
    const cleanRoomName = sanitizeInput(rawRoomName);
    if (cleanRoomName) {
      const payload = {
        gameType,
        roomName: cleanRoomName,
        user: {
          userId: getOrCreateUserId(),
          username: user.username,
          color: user.color,
          icon: user.icon,
          flag: user.flag,
        },
        allowSpectators:
          typeof opts.allowSpectators === "boolean"
            ? opts.allowSpectators
            : typeof user.allowSpectators === "boolean"
            ? user.allowSpectators
            : true,
      };
      if (opts.password) payload.password = sanitizeInput(opts.password);
      // Always include persistent userId
      //payload.user.userId = getOrCreateUserId();
      socket.emit("createRoom", payload);
      // try {
      //   localStorage.setItem("lastRoomId", "pending"); // Mark as pending until joined
      // } catch {}
      socket.emit("lobbyChat", {
        type: "event",
        text: `Room '${cleanRoomName}' was created by ${sanitizeInput(
          user.username
        )}.`,
        datetime: new Date().toISOString(),
        username: "System",
      });
    }
    //  trackGameChoice(gameType);
    //  goToCreateGameRoom();
  };

  // Send Lobby message - type is either "chat" or "event"
  //   const sendLobbyMessage = (text, type = "event") => {
  //     socket.emit("lobbyChat", {
  //       type: type,
  //       text: sanitizeInput(text),
  //       datetime: new Date().toISOString(),
  //       username: sanitizeInput(user.username) || "Unknown",
  //       color: type === "event" ? "black" : user.color,
  //       icon: type === "event" ? "🎉" : user.icon,
  //     });
  //   };

  // Wrap createRoom to send password and always include roomName
  // Accept payload from CreateRoom
  const onCreateRoom = (payload) => {
    createRoom({
      ...payload,
      password: requirePassword ? roomPassword : undefined,
      gameType,
      allowSpectators,
    });
  };

  // Wrapper for joinRoom to match RoomsList prop
  const onJoinRoom = (roomId, password, isSpectating) => {
    //console.log(
    //   "Joining room:",
    //   roomId,
    //   "with password:",
    //   password,
    //   "as spectator:",
    //   isSpectating
    // );
    if (password !== undefined) {
      joinRoom(roomId, password, isSpectating);
    } else {
      joinRoom(roomId, undefined, isSpectating);
    }
  };

  return (
    <>
    
      <Lobby
        setShowCreateRoomModal={setShowCreateRoomModal}
        lobbyData={lobbyData}
        onJoinRoom={onJoinRoom}
        joinRoomError={joinRoomError}
        setJoinRoomError={setJoinRoomError}
        gameType={gameType}
        setGameType={setGameType}
      />

      

      {showCreateRoomModal && (
        <Modal
          show={showCreateRoomModal}
          title="Create Room"
          onClose={() => setShowCreateRoomModal(false)}
        >
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto mx-auto">
            <CreateRoomModal
              closeCreateGame={() => setShowCreateRoomModal(false)}
              user={user}
              onCreateRoom={onCreateRoom}
              gameTypes={lobbyData.gameTypes}
              gameType={gameType}
              setGameType={setGameType}
              requirePassword={requirePassword}
              setRequirePassword={setRequirePassword}
              roomPassword={roomPassword}
              setRoomPassword={setRoomPassword}
              allowSpectators={allowSpectators}
              setAllowSpectators={setAllowSpectators}
            />
          </div>
        </Modal>
      )}
    </>
  );
}

export default LobbyContainer;
